import {
  getTradeRate,
  getTradingAssets,
  getTransactionHistory,
  getWalletAssets,
  initDigitalWallet,
  getAssets,
  getAssetWallet,
  getPortfolio,
  getUserOrders,
  getUserTransactionHistory,
  getMyFiatTransactions,
  getUserBanking,
  getUserBank,
  getNet,
  getPortfolioEstimate,
  getEightyEightBank,
  getFees,
  getBanks,
  getWalletAddresses,
  getPortfolioEstimateAssets,
  getMarketAssets,
  getPriceHistory,
  getUserPayTransactions,
} from "../../services/digitalWalletService";
import {
  getAssetPairs,
  getAllTradingAssets,
  getAllTradingData,
  getWalletAssetsData,
  getAllTradablePairsOptimized,
} from "../../services/tradingDataService";

import {
  getFaqCategories,
  getFaqs,
  getOrganisations,
  searchFaq,
} from "../../services/settingsService";
import {
  getNotifications,
  formatNotification,
} from "../../services/notificationService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useMemo, useEffect } from "react";
import { getUser } from "../../services/dashboardService";
import { get, updatePatch } from "../../services/apiService";
import { useTransactionTrackingStore } from "../../store/useTransactionTrackingStore";
import { getAssetsWithChains } from "../../services/tradingAssetsService";
import { useAuth } from "../../context/AuthContext";
import {
  createDepositQuote,
  getApplication,
  getAvalableAmount,
  getExistingApplication,
  getInvestmentPayWith,
  getInvestPortfolio,
  getInvestProduct,
  getInvestProducts,
  getLedgerEntries,
  getPendingLedgers,
  getProductTransactionHistory,
  getSingleInvestment,
  investPerformanceGraph,
} from "../../services/investService";

import {
  getAutoStackStats,
  getAutoStackTemplates,
  getUserAutoStacks,
  getSingleAutoStackStats,
  getUserSingleAutoStack,
  getAutoStackPurchases,
} from "../../services/autostackService";
import { getBankTransferReference } from "../../services/digitalWalletService";
import {
  getZar8YieldConfig,
  getZar8YieldEarnings,
  getZar8YieldHistory,
  getZar8YieldPosition,
  getZar8YieldPositions,
} from "../../services/zar8YieldService";
import {
  applyZar8MarketIconFallback,
  buildAssetImageMap,
} from "../../utils/assetIconFallbacks";

export const useFiatBalance = (selectedFiat) => {
  const { data: portfolioData } = usePortfolioEstimateAssets();

  return useQuery({
    queryKey: ["walletBalance", selectedFiat?._id, portfolioData?.timestamp],

    queryFn: async () => {
      if (!selectedFiat || !portfolioData) return null;

      const assets = portfolioData?.assets || [];
      const matchedAsset = assets.find(
        (asset) =>
          asset.assetId === selectedFiat._id ||
          asset._id === selectedFiat._id ||
          asset.asset?.toUpperCase() === selectedFiat.asset?.toUpperCase()
      );

      const availableAmount = matchedAsset?.forattedAvailableAmount || "0";
      const minWithdrawal =
        selectedFiat.withdrawalMinAmount?.$numberDecimal || "0";
      return { availableAmount, minWithdrawal };
    },
    enabled: !!selectedFiat && !!portfolioData,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};
export const useFaqCategories = () => {
  return useQuery({
    queryKey: ["faqCategories"],
    queryFn: async () => {
      const categories = await getFaqCategories();
      return categories;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useFaq = (categoryId, subCategoryId = null) => {
  return useQuery({
    queryKey: ["faq", categoryId, subCategoryId],
    queryFn: async () => {
      const data = await getFaqs(categoryId, subCategoryId);
      return data;
    },
    enabled: !!categoryId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useFaqSearch = (q, options = {}) => {
  const { category, limit = 10, offset = 0 } = options;
  const trimmed = (q || "").trim();
  return useQuery({
    queryKey: ["faqSearch", trimmed, category, limit, offset],
    queryFn: async () => searchFaq(trimmed, { category, limit, offset }),
    enabled: trimmed.length > 0,
    staleTime: 1000 * 60,
  });
};

export const useFiatAssets = () => {
  return useQuery({
    queryKey: ["fiatAssets"],
    queryFn: async () => {
      try {
        const data = await getAssets("Fiat", "no");

        return data.data.data;
      } catch (error) {
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useInvestProduct = (id) => {
  return useQuery({
    queryKey: ["investProduct", id],
    queryFn: async () => {
      try {
        const data = await getInvestProduct(id);

        return data.data.data;
      } catch (error) {
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!id,
  });
};
export const useApplicationEntry = (applicationId) => {
  return useQuery({
    queryKey: ["applicationEntry", applicationId],
    queryFn: async () => {
      try {
        const data = await getApplication(applicationId);
        console.log("data", data);
        return data.data.data;
      } catch (error) {
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!applicationId,
  });
};
export const useExistingApplication = (userId, productId) => {
  return useQuery({
    queryKey: ["existingApplication", userId, productId],
    queryFn: async () => {
      try {
        const data = await getExistingApplication(userId, productId);

        return data.data.data;
      } catch (error) {
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!userId && !!productId,
  });
};
export const useInvestGraph = (productId, principle, timeframe) => {
  return useQuery({
    queryKey: ["investGraph", productId, principle, timeframe],
    queryFn: async () => {
      try {
        const data = await investPerformanceGraph(
          productId,
          principle,
          timeframe
        );

        return data.data.data;
      } catch (error) {
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!productId && !!principle,
  });
};

export const useAssetNetwork = (chaiId) => {
  return useQuery({
    queryKey: ["assetNetwork", chaiId],
    queryFn: async () => {
      if (!chaiId) return [];
      const response = await getNet(chaiId);
      return response?.data?.data || [];
    },
    enabled: !!chaiId,
    staleTime: 1000 * 60 * 5,
  });
};

export const usePortfolioEstimate = (userId, startDate, endDate) => {
  return useQuery({
    queryKey: ["portfolioEstimate", userId, startDate, endDate],
    queryFn: async () => {
      const response = await getPortfolioEstimate(startDate, endDate);

      return response?.data?.data || { graphData: [] };
    },
    enabled: !!userId && !!startDate && !!endDate,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useSingleBank = (bid) => {
  return useQuery({
    queryKey: ["singleBank", bid],
    queryFn: async () => {
      if (!bid) return [];
      const response = await getUserBank(bid);
      return response?.data?.data || [];
    },
    enabled: !!bid,
    staleTime: 1000 * 60 * 5,
  });
};

export const useUserBanking = (userId) => {
  return useQuery({
    queryKey: ["userBanking", userId],
    queryFn: async () => {
      const response = await getUserBanking(userId);
      return response?.data?.data || [];
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
  });
};
export const useEightyEightBanks = () => {
  return useQuery({
    queryKey: ["eightyEightBanks"],
    queryFn: async () => {
      try {
        const response = await getEightyEightBank();
        return response?.data?.data;
      } catch (error) {}
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useHas88PayHistory = (userId) => {
  return useQuery({
    queryKey: ["has88PayHistory", userId],
    enabled: !!userId,
    queryFn: async () => {
      try {
        const response = await getUserPayTransactions(userId);
        const results = response?.data?.data?.results || [];
        return results.length > 0;
      } catch (error) {
        return false;
      }
    },
    staleTime: 1000 * 60 * 30,
  });
};

export const useAllBanks = () => {
  return useQuery({
    queryKey: ["allBanks"],
    queryFn: async () => {
      try {
        const allBanks = await getBanks();
        const response = allBanks.data.data.results;
        return response;
      } catch (error) {}
    },
    staleTime: 1000 * 60 * 5,
  });
};
export const useFees = () => {
  return useQuery({
    queryKey: ["fees"],
    queryFn: async () => {
      try {
        const response = await getFees("zar");
        return response?.data?.data;
      } catch (error) {}
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useUserTransactionHistory = (
  userId,
  enabled = true,
  { fetchAllPages = true } = {}
) => {
  return useQuery({
    queryKey: ["userTransactionHistory", userId, { fetchAllPages }],
    enabled: !!userId && enabled,
    queryFn: async () => {
      const [transactionHistoryData, fiatTransactionsData] = await Promise.all([
        getUserTransactionHistory(userId).then((res) => res.data.data),
        getMyFiatTransactions(1)
          .then((res) => res.data.data)
          .catch(() => ({ results: [] })),
      ]);

      let allResults = [...(transactionHistoryData.results || [])];

      if (fetchAllPages && transactionHistoryData.totalPages > 1) {
        const pageRequests = [];
        for (let i = 2; i <= transactionHistoryData.totalPages; i++) {
          pageRequests.push(getUserTransactionHistory(userId, i));
        }

        const responses = await Promise.all(pageRequests);
        responses.forEach((response) => {
          allResults.push(...response.data.data.results);
        });
      }

      const fiatResults = (fiatTransactionsData.results || []).map((fiat) => {
        let amountStr = "0";
        if (fiat.amount) {
          amountStr =
            typeof fiat.amount === "object" && fiat.amount.$numberDecimal
              ? fiat.amount.$numberDecimal
              : fiat.amount.toString();
        } else if (fiat.totalAmount) {
          amountStr =
            typeof fiat.totalAmount === "object" &&
            fiat.totalAmount.$numberDecimal
              ? fiat.totalAmount.$numberDecimal
              : fiat.totalAmount.toString();
        }

        const formattedAmount = parseFloat(amountStr)
          .toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
          .replace(/,/g, " ");

        return {
          _id: fiat._id,
          userId: fiat.userId,
          accountId: fiat.accountId,
          amount: `R ${formattedAmount}`,
          activity: fiat.type === "Deposit" ? "incoming" : "outgoing",
          asset: fiat.asset?.toLowerCase() || "zar",
          status: fiat.status,
          transactionType: "FiatTransaction",
          transactionSubtype: fiat.type?.toLowerCase() || "deposit",
          transactionRef: fiat._id,
          createdAt: fiat.createdAt || fiat.date,
        };
      });

      const merged = [...allResults, ...fiatResults];
      const uniqueResults = merged.filter(
        (item, index, self) =>
          index === self.findIndex((t) => t._id === item._id)
      );

      uniqueResults.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      return uniqueResults;
    },
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
};

export const useAvailableAssets = ({
  isSend,
  isEightyeightPay,
  investArray,
  balanceParams,
  balanceParamsEightEight,
  enabled = true,
}) => {
  const { data: portfolioData } = usePortfolioEstimateAssets();

  return useQuery({
    queryKey: [
      "availableAssets",
      isSend,
      isEightyeightPay,
      balanceParams,
      balanceParamsEightEight,
      investArray,
      portfolioData?.timestamp,
    ],
    enabled: enabled && !!portfolioData,
    queryFn: async () => {
      let availableAmount = [];

      if (!portfolioData?.assets) {
        return [];
      }

      if (isSend) {
        if (balanceParams?.type) {
          availableAmount = portfolioData.assets.filter(
            (asset) =>
              asset.currencyType === balanceParams.type ||
              asset.type === balanceParams.type
          );
        } else {
          availableAmount = portfolioData.assets;
        }
        return availableAmount.length > 0 ? availableAmount : [];
      }

      if (isEightyeightPay) {
        if (balanceParamsEightEight?.type) {
          availableAmount = portfolioData.assets.filter(
            (asset) =>
              asset.currencyType === balanceParamsEightEight.type ||
              asset.type === balanceParamsEightEight.type
          );
        } else {
          availableAmount = portfolioData.assets;
        }
        return availableAmount.length > 0 ? availableAmount : [];
      }

      if (!isSend && !isEightyeightPay) {
        availableAmount = portfolioData.assets;
        return availableAmount.length > 0 ? availableAmount : [];
      }

      return [];
    },
    onError: (err) => {
      console.error("Error fetching available assets:", err);
    },
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export const useAvailableBalances = (userId) => {
  const { data: portfolioData } = usePortfolioEstimateAssets();

  return useQuery({
    queryKey: ["availableBalances", userId, portfolioData?.timestamp],
    enabled: !!userId && !!portfolioData,
    queryFn: async () => {
      const assets = portfolioData?.assets || [];

      return assets.map((asset) => ({
        ...asset,
        availableAmountFormatted:
          asset.forattedAvailableAmount ||
          `${asset.availableAmount} ${asset.asset?.toUpperCase()}`,
        assetEstimatedPrice:
          typeof asset.assetEstimatedPrice === "string"
            ? asset.assetEstimatedPrice
            : asset.assetEstimatedPrice?.toString() || "0",
      }));
    },
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};
export const useProductHistory = (ledgerId) => {
  return useQuery({
    queryKey: ["productTransactionHistory", ledgerId],
    enabled: !!ledgerId,
    queryFn: async () => {
      const response = await getProductTransactionHistory(ledgerId);
      return response.data.data;
    },
    staleTime: 1000 * 60 * 2,
  });
};
export const useDepositQuote = (data) => {
  return useQuery({
    queryKey: ["depositQuote", data],
    enabled: !!data,
    queryFn: async () => {
      const response = await createDepositQuote(data);
      return response.data.data;
    },
    staleTime: 1000 * 60 * 2,
  });
};
export const useInvestmentPayWith = (ledgerId, assetName) => {
  return useQuery({
    queryKey: ["depositQuote", ledgerId, assetName],
    enabled: !!ledgerId && !!assetName,
    queryFn: async () => {
      const response = await getInvestmentPayWith(ledgerId, assetName);
      return response.data.data;
    },
    staleTime: 1000 * 60 * 2,
  });
};

export const useInvestProducts = () => {
  return useQuery({
    queryKey: ["investProducts"],
    queryFn: async () => {
      try {
        const data = await getInvestProducts();

        return data.data.data;
      } catch (error) {}
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useZar8YieldPositions = (params = {}, enabled = true) => {
  return useQuery({
    queryKey: ["zar8YieldPositions", params],
    queryFn: async () => {
      const response = await getZar8YieldPositions(params);
      return response.data.data;
    },
    enabled,
    staleTime: 1000 * 60,
  });
};

export const useZar8YieldPosition = (positionId, enabled = true) => {
  return useQuery({
    queryKey: ["zar8YieldPosition", positionId],
    queryFn: async () => {
      const response = await getZar8YieldPosition(positionId);
      return response.data.data;
    },
    enabled: enabled && !!positionId,
    staleTime: 1000 * 60,
  });
};

export const useZar8YieldHistory = (params = {}, enabled = true) => {
  return useQuery({
    queryKey: ["zar8YieldHistory", params],
    queryFn: async () => {
      const response = await getZar8YieldHistory(params);
      return response.data.data;
    },
    enabled,
    staleTime: 1000 * 60,
  });
};

export const useZar8YieldEarnings = (enabled = true) => {
  return useQuery({
    queryKey: ["zar8YieldEarnings"],
    queryFn: async () => {
      const response = await getZar8YieldEarnings();
      return response.data.data;
    },
    enabled,
    staleTime: 1000 * 60,
  });
};

export const useZar8YieldConfig = (enabled = true) => {
  return useQuery({
    queryKey: ["zar8YieldConfig"],
    queryFn: async () => {
      const response = await getZar8YieldConfig();
      return response.data.data;
    },
    enabled,
    staleTime: 1000 * 60 * 5,
  });
};

export const useNotifications = (page = 1, limit = 20, category = null) => {
  return useQuery({
    queryKey: ["notifications", page, limit, category],
    queryFn: async () => {
      try {
        const response = await getNotifications(page, limit, category);
        const data = response?.data?.data || response?.data || {};

        const notifications = (data.results || data || []).map(
          formatNotification
        );

        return {
          results: notifications,
          totalCount: data.totalCount || notifications.length,
          totalUnread: data.totalUnread || 0,
          page: data.page || page,
          limit: data.limit || limit,
        };
      } catch (error) {
        console.error("Error fetching notifications:", error);
        return { results: [], totalCount: 0, totalUnread: 0 };
      }
    },
    staleTime: 1000 * 60 * 2,
    refetchInterval: 1000 * 30, // Poll every 30 seconds for new notifications
    refetchOnMount: "always", // Always fetch fresh data when component mounts
  });
};
export const useLedgerEntries = (userId) => {
  return useQuery({
    queryKey: ["ledgerEntries", userId],
    queryFn: async () => {
      try {
        const response = await getLedgerEntries(userId);
        return response.data.data;
      } catch (error) {}
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
export const usePendingLedgers = (userId) => {
  return useQuery({
    queryKey: ["pendingLedgers", userId],
    queryFn: async () => {
      try {
        const response = await getPendingLedgers(userId);
        return response.data.data;
      } catch (error) {}
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
export const useInvestmentEntry = (ledgerId) => {
  return useQuery({
    queryKey: ["invetmentEntries", ledgerId],
    queryFn: async () => {
      try {
        const response = await getSingleInvestment(ledgerId);
        return response.data.data;
      } catch (error) {}
    },
    enabled: !!ledgerId,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
export const useInvestmentBalance = (ledgerId) => {
  return useQuery({
    queryKey: ["investmentBalance", ledgerId],
    queryFn: async () => {
      try {
        const response = await getAvalableAmount(ledgerId);
        console.log("response", response);
        return response.data.data;
      } catch (error) {}
    },
    enabled: !!ledgerId,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useInvestPortfolio = (userId) => {
  return useQuery({
    queryKey: ["investPortfolio", userId],
    queryFn: async () => {
      const response = await getInvestPortfolio(userId);

      return response.data.data;
    },
    staleTime: 1000 * 60 * 3,
    enabled: !!userId,
  });
};

export const useOrders = (userId) => {
  return useQuery({
    queryKey: ["userOrders", userId],
    enabled: !!userId,
    queryFn: async () => {
      const response = await getUserOrders(userId);

      return response.data.data.results;
    },
    staleTime: 1000 * 60 * 2,
  });
};
export const useCryptoAddresses = () => {
  return useQuery({
    queryKey: ["cryptoAddress"],
    queryFn: async () => {
      const response = await getWalletAddresses();
      return response.data.data.address;
    },
    staleTime: 1000 * 60 * 2,
  });
};

export const useAssetEstimate = (userId, asset) => {
  return useQuery({
    queryKey: ["assetEstimate", userId, asset?.asset],
    enabled: !!userId && !!asset,
    queryFn: async () => {
      const getEstData = {
        userId,
        asset: asset?.asset,
      };
      const response = await getPortfolio(getEstData);
      return response.data.data;
    },
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};
export const useOrganisations = () => {
  return useQuery({
    queryKey: ["donationOrganisation"],
    queryFn: async () => {
      try {
        const response = await getOrganisations();
        return response.data.data.results;
      } catch (error) {}
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useNetworksByAssets = (tradingAssets, setLoading) => {
  return useQuery({
    queryKey: [
      "networksByAssets",
      tradingAssets?.map((a) => a.assetId || a._id),
    ],
    enabled: !!tradingAssets?.length,
    queryFn: async () => {
      try {
        const { getAllChains } = await import(
          "../../services/tradingDataService"
        );
        const allChains = await getAllChains();

        const assetIds = tradingAssets.map(
          (asset) => asset.assetId || asset._id
        );
        const filteredChains = allChains.filter((chain) =>
          assetIds.includes(chain.assetId)
        );

        return filteredChains;
      } catch (error) {
        console.error("Error fetching networks:", error);
        return [];
      }
    },
    onSuccess: () => {
      if (setLoading) setLoading(false);
    },
    onError: (error) => {
      console.error("Error in useNetworksByAssets:", error);
      if (setLoading) setLoading(false);
    },
  });
};

export const useWalletByNetwork = (
  selectedNetwork,
  onWalletSelect,
  setLoadingAsset
) => {
  return useQuery({
    queryKey: [
      "walletByNetwork",
      selectedNetwork?.assetId,
      selectedNetwork?._id,
    ],
    queryFn: async () => {
      if (!selectedNetwork?.assetId || !selectedNetwork?._id) {
        throw new Error("Invalid network data");
      }

      const walletData = {
        assetId: selectedNetwork.assetId,
        chainId: selectedNetwork._id,
      };

      const response = await getAssetWallet(walletData);
      return response?.data?.data || [];
    },
    enabled: !!selectedNetwork?.assetId && !!selectedNetwork?._id,
    onSuccess: (data) => {
      const noWallet = data.length === 0 ? 1 : 0;

      if (onWalletSelect) {
        onWalletSelect(data, noWallet, selectedNetwork);
      }

      setLoadingAsset(false);
    },
    onError: (error) => {
      if (onWalletSelect) {
        onWalletSelect([], 1, selectedNetwork);
      }

      setLoadingAsset(false);
    },
  });
};

export const useFetchPairs = (assetId) => {
  return useQuery({
    queryKey: ["assetPairs", assetId],
    queryFn: async () => {
      try {
        const data = await getAssetPairs(assetId);
        return data || [];
      } catch (err) {
        console.error("Error fetching pairs:", err);
        return [];
      }
    },
    enabled: !!assetId,
    staleTime: 1000 * 60 * 5,
  });
};
export const useCryptoAssets = () => {
  return useQuery({
    queryKey: ["cryptoAssets"],
    queryFn: async () => {
      try {
        const data = await getAllTradingAssets("Crypto");
        return data?.data || [];
      } catch (error) {
        console.error("Error fetching crypto assets:", error);
        return [];
      }
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    structuralSharing: true,
  });
};

export const useAllTradingData = (type = null) => {
  return useQuery({
    queryKey: ["allTradingData", type],
    queryFn: async () => {
      try {
        return await getAllTradingData(type);
      } catch (error) {
        console.error("Error fetching trading data:", error);
        return { tradingAssets: null, walletAssets: null };
      }
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useMarketAssets = () => {
  return useQuery({
    queryKey: ["marketAssets"],
    queryFn: async () => {
      const data = await getMarketAssets();
      let assetsMetadataList = [];

      try {
        const assetsMetadata = await getAssets();
        assetsMetadataList = assetsMetadata?.data?.data || [];
      } catch {
        assetsMetadataList = [];
      }

      return applyZar8MarketIconFallback(data.data.data, assetsMetadataList);
    },
    staleTime: 1000 * 60 * 5,
    retry: 2,
    retryDelay: 1000,
  });
};

export const usePriceHistory = (symbol, interval, range) => {
  return useQuery({
    queryKey: ["priceHistory", symbol, interval, range],
    queryFn: async () => {
      const data = await getPriceHistory(symbol, interval, range);
      return data?.data?.data ?? null;
    },
    enabled: Boolean(symbol),
    staleTime: 1000 * 60 * 2,
  });
};
export const useAllOwnedAssets = () => {
  return useQuery({
    queryKey: ["allOwnedAssets"],
    queryFn: async () => {
      try {
        const data = await getWalletAssetsData();
        return data?.data || [];
      } catch (error) {
        console.error("Error fetching owned assets:", error);
        return [];
      }
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useTradeRate = (selectedAssetId, action, pair, isPostTrade) => {
  const normalizedPostTrade = isPostTrade === true;

  return useQuery({
    queryKey: ["tradeRate", selectedAssetId, action, pair, normalizedPostTrade],
    queryFn: async () => {
      const data = await getTradeRate(
        selectedAssetId,
        action,
        pair,
        normalizedPostTrade
      );

      return data.data.data;
    },
    enabled:
      selectedAssetId !== undefined &&
      selectedAssetId !== null &&
      action !== undefined &&
      action !== null &&
      pair !== undefined &&
      pair !== null,
    refetchInterval: 30000,
    staleTime: 10000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    networkMode: "online",
    structuralSharing: true,
  });
};

export const useFetchAllTradablePairs = () => {
  const { data: cryptoAssets, isSuccess: isCryptoSuccess } = useCryptoAssets();

  return useQuery({
    queryKey: ["allTradablePairs"],
    queryFn: async () => {
      try {
        return await getAllTradablePairsOptimized();
      } catch (err) {
        console.error("Error fetching all tradable pairs:", err);
        return {};
      }
    },
    enabled: isCryptoSuccess && !!cryptoAssets && cryptoAssets.length > 0,
    staleTime: 1000 * 60 * 5,
  });
};

export const useWalletBalance = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ["portfolioEstimateAssets"],
    queryFn: async () => {
      const response = await getPortfolioEstimateAssets();
      return response || null;
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    gcTime: 1000 * 60 * 5,
    retry: (failureCount, error) => {
      if (error?.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
    networkMode: "online",
    structuralSharing: true,
    select: (data) => {
      const portfolioData = data?.data?.data || {};
      return {
        usdtEstimate: portfolioData?.usdtEstimate || 0,
        zarEstimate: portfolioData?.zarEstimate || 0,
        portfolioEstimate: portfolioData?.portfolioEstimate || "0.00",
        portfolioEstimateRaw: portfolioData?.portfolioEstimateRaw || 0,
        assets: portfolioData?.assets || [],
        timestamp: portfolioData?.timestamp,
      };
    },
  });
};

export const useSubAccounts = (userId) => {
  const { userProfile } = useAuth();

  return useQuery({
    queryKey: ["subAccounts", userId, userProfile?.subAccountDetails],
    queryFn: () => {
      const subAccountDetails = userProfile?.subAccountDetails || [];
      return subAccountDetails;
    },
    enabled: !!userId && !!userProfile,
    staleTime: 0,
    initialData: userProfile?.subAccountDetails || [],
  });
};
export const useActiveAccount = (userId, accountId) => {
  const { userProfile } = useAuth();

  return useQuery({
    queryKey: [
      "activeAccount",
      userId,
      accountId,
      userProfile?.subAccountDetails,
    ],
    queryFn: () => {
      const subAccountDetails = userProfile?.subAccountDetails || [];
      const account = subAccountDetails.find(
        (acc) => acc._id === accountId || acc.accountId === accountId
      );
      return account || null;
    },
    enabled: !!userId && !!accountId && !!userProfile,
    staleTime: 0,
    initialData:
      userProfile?.subAccountDetails?.find(
        (acc) => acc._id === accountId || acc.accountId === accountId
      ) || null,
  });
};
export const usePortfolioEstimateAssets = () => {
  const { isAuthenticated } = useAuth();

  const { data: fullResponse, ...rest } = useQuery({
    queryKey: ["portfolioEstimateAssets"],
    queryFn: async () => {
      const response = await getPortfolioEstimateAssets();
      return response || null;
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    gcTime: 1000 * 60 * 5,
    retry: (failureCount, error) => {
      if (error?.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
    select: (data) => data?.data?.data || null,
    networkMode: "online",
    structuralSharing: true,
  });

  return { data: fullResponse, ...rest };
};

/**
 * @param {{ skip?: boolean }} [queryOptions] When `skip` is true, the usable-assets
 * query is disabled (e.g. business suite uses business wallet data instead).
 */
export const useWalletAssets = (queryOptions = { skip: false }) => {
  const skip = queryOptions?.skip === true;
  const { data: portfolioData } = usePortfolioEstimateAssets();

  return useQuery({
    queryKey: ["usableAssets", skip ? "off" : "on"],
    queryFn: async () => {
      try {
        const assetsMetadata = await getAssets();
        const assetsMetadataList = assetsMetadata?.data?.data || [];

        const assetImageMap = buildAssetImageMap(assetsMetadataList);

        const assetsForUse = ((portfolioData || {})?.assets || []).map(
          (asset) => {
            const assetKey = asset.asset?.toUpperCase();
            return {
              id: asset.assetId || asset.asset || asset.assetName,
              asset: asset.asset,
              assetName: asset.assetName,
              image: asset.assetIcon || assetImageMap.get(assetKey) || "",
              availableAmount: asset.availableAmount,
              forattedAvailableAmount: asset.forattedAvailableAmount,
              valueInUsdt: asset.valueInUsdt,
              valueInZar: asset.valueInZar,
              valueInLocalCurrency: asset.valueInLocalCurrency,
              currencySymbol: asset.currencySymbol,
              currencyType: asset.currencyType || asset.type,
              type: asset.type || asset.currencyType,
              assetEstimatedPrice: asset.assetEstimatedPrice,
              buyRate: asset.buyRate,
              sellRate: asset.sellRate,
            };
          }
        );

        return {
          assets: assetsForUse,
          usdtEstimate: (portfolioData || {})?.usdtEstimate || 0,
          zarEstimate: (portfolioData || {})?.zarEstimate || 0,
          portfolioEstimate: (portfolioData || {})?.portfolioEstimate || "0.00",
          portfolioEstimateRaw:
            (portfolioData || {})?.portfolioEstimateRaw || 0,
          timestamp: (portfolioData || {})?.timestamp,
        };
      } catch (err) {
        console.error("Error fetching wallet assets:", err);
        try {
          const data = await getAssets();
          return {
            assets: data.data.data,
            usdtEstimate: 0,
            zarEstimate: 0,
            portfolioEstimate: "0.00",
            portfolioEstimateRaw: 0,
          };
        } catch (fallbackErr) {
          throw fallbackErr;
        }
      }
    },
    enabled: !skip && !!portfolioData,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    gcTime: 1000 * 60 * 5,
    structuralSharing: true,
  });
};

export const useQueryUserBalance = (authId, initialData) => {
  const { isAuthenticated } = useAuth();

  const { data, ...rest } = useQuery({
    queryKey: ["portfolioEstimateAssets"],
    queryFn: async () => {
      const response = await getPortfolioEstimateAssets();
      return response || null;
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    gcTime: 1000 * 60 * 5,
    retry: (failureCount, error) => {
      if (error?.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
    networkMode: "online",
    structuralSharing: true,
    initialData,
  });
  const balance = data || initialData;
  return { balance, ...rest };
};

export const useQueryDigitalWallet = (authId, initialData) => {
  const { data: response, ...rest } = useQuery({
    queryKey: ["currencies", authId],
    queryFn: ({ queryKey }) => {
      const [, authId] = queryKey;
      return initDigitalWallet(authId);
    },
    staleTime: 15000,
  });

  const { digitalWalletData, defaultFiat } = useMemo(() => {
    return response
      ? {
          digitalWalletData: {
            ...response,
            balances: {
              assetBalances: {
                crypto: Array.isArray(
                  response?.data?.data?.balances?.assetBalances?.crypto
                )
                  ? response?.data?.data?.balances?.assetBalances?.crypto
                  : Object.values(
                      response?.data?.data?.balances?.assetBalances?.crypto ||
                        {}
                    ),
                fiat: Array.isArray(
                  response?.data?.data?.balances?.assetBalances?.fiat
                )
                  ? response?.data?.data?.balances?.assetBalances?.fiat
                  : Object.values(
                      response?.data?.data?.balances?.assetBalances?.fiat || {}
                    ),
              },
              portfolioEstimate:
                response?.data?.data?.balances?.portfolioEstimate || "0.00",
            },
          },
          defaultFiat:
            response?.data?.data?.balances?.assetBalances?.fiat?.[
              response.defaultCurrency
            ],
        }
      : { digitalWalletData: initialData, defaultFiat: initialData };
  }, [response, initialData]);
  return { digitalWalletData, defaultFiat, ...rest };
};

export const useQueryUserProfile = (initialData) => {
  const { data, ...rest } = useQuery({
    queryKey: ["userProfile"],
    queryFn: getUser,
    staleTime: 15000,
  });
  const user = data || initialData;
  return { user, ...rest };
};

export const useQueryTradingAssets = (authId, initialData) => {
  const { data, isLoading, ...rest } = useQuery({
    queryKey: ["tradingAssets", authId],
    queryFn: ({ queryKey }) => {
      const [, authId] = queryKey;
      return getTradingAssets(authId);
    },
    staleTime: 15000,
  });
  const tradingAssets = data || initialData;
  return { tradingAssets, isLoading, ...rest };
};

export const useQueryWalletAssets = (authId, initialData) => {
  const { data, isLoading, ...rest } = useQuery({
    queryKey: ["walletAssets", authId],
    queryFn: ({ queryKey }) => {
      const [, authId] = queryKey;
      return getWalletAssets(authId);
    },
    staleTime: 15000,
  });
  const assets = data || initialData;
  return { assets, isLoading, ...rest };
};

export const useQueryTransactionHistory = (authId, initialData) => {
  const { data, isLoading, ...rest } = useQuery({
    queryKey: ["transactionHistory", authId],
    queryFn: ({ queryKey }) => {
      const [, authId] = queryKey;
      return getTransactionHistory(authId);
    },
    staleTime: 15000,
  });
  const transactionHistory = data || initialData;
  return { transactionHistory, isLoading, ...rest };
};

export const useQueryAssetsWithChains = (type, initialData) => {
  const { data, isLoading, ...rest } = useQuery({
    queryKey: ["assetsWithChains", type],
    queryFn: () => getAssetsWithChains(type),
    staleTime: 15000,
  });
  const assets = data || initialData;
  return { assets, isLoading, ...rest };
};

export const useQueryTradeRate = (
  selectedCrypto,
  actionLabel,
  pair,
  initialData
) => {
  const { user } = useAuth();
  const authId = user?.sub || "";

  const queryKey = [
    "queryTradeRate",
    authId,
    selectedCrypto.assetId,
    actionLabel?.toLowerCase(),
    pair,
  ];

  const queryFn = ({ queryKey }) => {
    const [, authId, assetId, tradeType, pair] = queryKey;
    return getTradeRate(authId, assetId, tradeType, pair);
  };

  const { data, isLoading, ...rest } = useQuery({
    queryKey,
    queryFn,
    staleTime: 15000,
  });
  const tradeRate = data ? data.rate : initialData;
  return { tradeRate, isLoading, ...rest };
};

export const useQueryMutation = (mutationFn, key) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: Array.isArray(key) ? key : [key],
        refetchType: "all",
      });
    },
  });
};

export const useAutoStackStats = () => {
  return useQuery({
    queryKey: ["autoStackStats"],
    queryFn: async () => {
      const response = await getAutoStackStats();
      return response.data.data;
    },
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 60,
  });
};

export const useSingleAutoStackStats = (stackId) => {
  return useQuery({
    queryKey: ["singleAutoStackStats", stackId],
    queryFn: async () => {
      try {
        const response = await getSingleAutoStackStats(stackId);
        return response.data.data;
      } catch (error) {
        throw error;
      }
    },
  });
};

export const useAutoStackTemplates = () => {
  return useQuery({
    queryKey: ["autoStackTemplates"],
    queryFn: async () => {
      try {
        const response = await getAutoStackTemplates();
        return response.data.data;
      } catch (error) {
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useUserAutoStacks = () => {
  return useQuery({
    queryKey: ["userAutoStacks"],
    queryFn: async () => {
      try {
        const response = await getUserAutoStacks();
        return response.data.data;
      } catch (error) {
        throw error;
      }
    },
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 60,
  });
};

export const useUserSingleAutoStack = (stackId) => {
  return useQuery({
    queryKey: ["userSingleAutoStack", stackId],
    queryFn: async () => {
      const response = await getUserSingleAutoStack(stackId);
      return response.data.data;
    },
  });
};

export const useAutoStackPurchases = (stackId, page = 1, limit = 10) => {
  return useQuery({
    queryKey: ["autoStackPurchases", stackId, page, limit],
    queryFn: async () => {
      try {
        const response = await getAutoStackPurchases(stackId, page, limit);
        return response.data.data;
      } catch (error) {
        throw error;
      }
    },
    enabled: !!stackId,
  });
};

export const useBankTransferReference = () => {
  return useQuery({
    queryKey: ["bankTransferReference"],
    queryFn: async () => {
      const response = await getBankTransferReference();
      return response.data.data;
    },
  });
};

export const useActiveTransactions = () => {
  const setActiveTransactions = useTransactionTrackingStore(
    (s) => s.setActiveTransactions
  );

  const query = useQuery({
    queryKey: ["activeTransactions"],
    queryFn: async () => {
      const response = await get("/api/v2/crypto-transactions/active");
      return response?.data?.data || [];
    },
    refetchInterval: 30000,
    staleTime: 10000,
  });

  // Update store when data changes (React Query v5 compatible)
  // Clear store on error/empty to prevent stale websocket data persisting
  useEffect(() => {
    if (query.data) {
      setActiveTransactions(query.data);
    } else if (query.isError) {
      setActiveTransactions([]);
    }
  }, [query.data, query.isError, setActiveTransactions]);

  return query;
};

export const useDismissTransaction = () => {
  const queryClient = useQueryClient();
  const removeTransaction = useTransactionTrackingStore(
    (s) => s.removeTransaction
  );

  return useMutation({
    mutationFn: async (transactionId) => {
      return updatePatch(
        `/api/v2/crypto-transactions/${transactionId}/dismiss`
      );
    },
    onSuccess: (_, transactionId) => {
      removeTransaction(transactionId);
      queryClient.invalidateQueries({ queryKey: ["activeTransactions"] });
    },
  });
};

// Feature flag hooks moved to featureFlagsQueries.js
export {
  useAllFeatureFlags,
  useFeatureFlag,
  useFeatureFlagsByCategory,
} from "./featureFlagsQueries";
